import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatMoney } from "@/lib/formatters"
import { ReportList, ReportRow } from "@/features/staff/reports/report-block"
import { formatShare, tStats } from "@/features/staff/stats/format"
import type { StatsLeak } from "@/features/staff/stats/types"

/**
 * Discounts, cancellations, refunds and reopenings, as a share of the window's
 * net sales.
 *
 * **None of these is a deduction from the sales figure above.** A discount is
 * already inside it, a refund is already outside it, and a reopening moves no
 * money at all. The control report's own header says so at length; this card
 * shows the trend and sends the reader to `/reports` for who and why, rather
 * than reproducing half of that screen with none of its actors.
 *
 * A leak with no amount — reopenings — says so in words. Rendering it as $0.00
 * would read as "nothing was at stake", which is the opposite of what the row is
 * for.
 */
export function StatsLeaksBlock({ leaks }: { leaks: StatsLeak[] }) {
  if (leaks.length === 0) return null

  return (
    <Card density="compact" data-testid="stats-leaks">
      <CardHeader className="border-b">
        <CardTitle>{tStats("leaks.title")}</CardTitle>
        <CardDescription>{tStats("notes.leaks_basis")}</CardDescription>
      </CardHeader>

      <CardContent>
        <ReportList testId="stats-leaks-list">
          {leaks.map((leak) => (
            <ReportRow
              key={leak.key}
              testId={`stats-leak-${leak.key}`}
              label={leak.label}
              sublabel={
                leak.share === null
                  ? tStats("leaks.no_amount")
                  : tStats("leaks.share", { share: formatShare(leak.share) })
              }
              meta={tStats("leaks.count", { count: leak.count })}
              amount={leak.amount === null ? null : formatMoney(leak.amount)}
            />
          ))}
        </ReportList>
      </CardContent>
    </Card>
  )
}
